import React, { useState } from "react";
import NiceModal, { NiceModalHandler, useModal } from "@ebay/nice-modal-react";
import Modal from "src/components/Modal";
import {
  Button as UiButton,
  CodeInput,
  DialogActions,
} from "src/components/ui";
import { FieldError } from "src/components/Form/styles/form";
import { useFormik } from "formik";
import { useTimer } from "./hooks";
import { validationSchema } from "./validationSchema";
import { otpErrorMessage } from "./errorMessage";

export default NiceModal.create(
  ({
    resend,
    verify,
    onSuccess,
    onFailure,
    onDismiss,
  }: {
    resend?: () => Promise<any>;
    verify: (otp: string) => Promise<any>;
    onSuccess?: (data: any) => Promise<any>;
    /** Backed out without verifying — the step that opened this can come
        back rather than leaving the reader on the page behind it. */
    onDismiss?: () => void;
    onFailure?: (
      error: any,
      modalInstance: NiceModalHandler<Record<string, unknown>>,
    ) => Promise<any>;
  }) => {
    // States
    const [state, setState] = useState({
      isLoading: false,
      error: null,
      timerActive: false,
    });
    const [timeLeft, resetTimer] = useTimer(30, () =>
      setState((s) => ({ ...s, timerActive: false })),
    );
    const modal = useModal();
    const onCancel = () => {
      modal.hide();
      onDismiss?.();
    };

    const formik = useFormik({
      initialValues: {
        otp: "",
        error: null,
      },
      validationSchema,
      onSubmit: async (values) => {
        const otp = values.otp.replace(/\s+/g, "");
        try {
          const res = await verify(otp);
          if (onSuccess) onSuccess(res?.data);
          modal.hide();
          modal.remove();
        } catch (error) {
          formik.setFieldError("otp", otpErrorMessage(error));
          formik.setSubmitting(false);

          if (onFailure) onFailure(error.message, modal);
        }
      },
    });

    const resendOtp = async () => {
      setState({ ...state, isLoading: true, error: null });
      try {
        await resend();
        resetTimer();
        setState({ ...state, isLoading: false, timerActive: true });
      } catch (error) {
        setState({ ...state, isLoading: false, error: error.message });
      }
    };

    const _footer = () => [
      /* The shared action row: it was a block half the dialog wide holding a
         responsive grid, so two buttons sat in the middle of a foot band
         sized for a page. */
      <DialogActions key="otp-actions">
        {resend && (
          <UiButton
            variant="secondary"
            onClick={resendOtp}
            disabled={state.timerActive || state.isLoading}
            loading={state.isLoading}
          >
            {state.timerActive ? `${timeLeft} seconds` : "Resend code"}
          </UiButton>
        )}
        <UiButton
          variant="primary"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || formik.isSubmitting || !verify}
          loading={formik.isSubmitting}
        >
          Verify
        </UiButton>
      </DialogActions>,
    ];

    return (
      <Modal
        title={
          <>
            <div>Verify it is you</div>
            <p className="fina-modal-description">
              We sent a six-digit code to your email and by SMS.
            </p>
          </>
        }
        /* A dialog for one six-digit field does not need 70% of the window. */
        width={440}
        visible={modal.visible}
        onCancel={onCancel}
        afterClose={() => modal.remove()}
        footer={_footer()}
      >
        {/* The shared six-cell box, which is what every other code entry in
            the product uses — the webapp's email verification, the invest
            flow and components/OTP all draw this one. This dialog was the
            last holdout: a 156px `react-input-mask` underline whose six
            digits were faked with whitespace inside the mask
            ("9   9   9   9   9   9"), with its label jammed against its left
            edge, floating in a panel imported from the invest PAGE's
            stylesheet. The panel is gone with it — a dialog this size does
            not need a page's layout inside it.

            The sentence in the head is this field's label, so the name here
            is given to a screen reader rather than printed over the row. */}
        <CodeInput
          autoFocus
          label="One-time code"
          name="otp"
          id="otp"
          value={formik.values.otp}
          invalid={Boolean(formik.touched.otp && formik.errors.otp)}
          onChange={(value) => formik.setFieldValue("otp", value)}
          onBlur={formik.handleBlur}
        />
        {formik.touched.otp && formik.errors.otp ? (
          <FieldError role="alert">{String(formik.errors.otp)}</FieldError>
        ) : null}
      </Modal>
    );
  },
);
